import { useState, useCallback, useMemo, useEffect } from 'react';
import {
    GripVertical,
    Plus,
    MoreHorizontal,
    Edit2,
    Trash2,
    Copy,
    Star,
    Share2,
    Check,
    Loader2,
    ChevronDown,
    ChevronRight,
    User,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    useSavedViews,
    useCreateSavedView,
    useUpdateSavedView,
    useDeleteSavedView,
    useDuplicateSavedView,
    useReorderViews,
    useToggleFavoriteView,
    useReorderFavoriteViews,
} from '@/hooks/use-views';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth-store';
import {
    createSavedViewSchema,
    type SavedView,
    type ViewConfig,
    type CreateSavedViewInput,
    type ViewType,
    type CustomField,
} from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { KanbanCardFieldsPicker } from './kanban';

// ============================================
// VIEW ITEM
// ============================================

interface ViewItemProps {
    view: SavedView;
    isActive: boolean;
    onSelect: (view: SavedView) => void;
    onEdit: (view: SavedView) => void;
    onDelete: (viewId: string) => void;
    onDuplicate: (viewId: string) => void;
    onSetDefault: (view: SavedView) => void;
    onToggleFavorite: (viewId: string) => void;
    isDragging?: boolean;
    onDragStart: (e: React.DragEvent, view: SavedView) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent, targetView: SavedView) => void;
    onDragEnd: () => void;
    isOwner: boolean;
}

function ViewItem({
    view,
    isActive,
    onSelect,
    onEdit,
    onDelete,
    onDuplicate,
    onSetDefault,
    onToggleFavorite,
    isDragging,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    isOwner,
}: ViewItemProps) {
    const { t } = useI18n();
    const [menuOpen, setMenuOpen] = useState(false);

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuOpen(true);
    };

    return (
        <div
            className={`group flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${isActive
                ? 'bg-primary/10 text-primary'
                : 'hover:bg-surface-hover text-foreground-secondary'
                } ${isDragging ? 'opacity-50' : ''}`}
            onClick={() => onSelect(view)}
            onContextMenu={handleContextMenu}
            draggable
            onDragStart={(e) => onDragStart(e, view)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, view)}
            onDragEnd={onDragEnd}
        >
            <div className="flex items-center gap-1 min-w-0 flex-1">
                <div
                    className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-surface-hover rounded touch-none"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <GripVertical className="h-3.5 w-3.5 text-foreground-muted" />
                </div>
                <span className="text-sm truncate">{view.name}</span>
                {view.isDefault && (
                    <Star className="h-3 w-3 flex-shrink-0 text-amber-500 fill-amber-500" />
                )}
            </div>

            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100">
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onToggleFavorite(view.id)}>
                        <Star className={`h-4 w-4 mr-2 ${view.isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
                        {view.isFavorite ? t('views.removeFromFavorites') : t('views.addToFavorites')}
                    </DropdownMenuItem>
                    {isOwner && (
                        <DropdownMenuItem onClick={() => onEdit(view)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            {t('views.editView')}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onDuplicate(view.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        {t('views.duplicateView')}
                    </DropdownMenuItem>
                    {isOwner && !view.isDefault && (
                        <DropdownMenuItem onClick={() => onSetDefault(view)}>
                            <Star className="h-4 w-4 mr-2" />
                            {t('views.setDefault')}
                        </DropdownMenuItem>
                    )}
                    {isOwner && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={() => onDelete(view.id)}
                                className="text-destructive focus:text-destructive"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('common.delete')}
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

// ============================================
// COLLAPSIBLE SECTION
// ============================================

interface CollapsibleSectionProps {
    title: string;
    icon: React.ReactNode;
    isOpen: boolean;
    onToggle: () => void;
    count: number;
    children: React.ReactNode;
}

function CollapsibleSection({
    title,
    icon,
    isOpen,
    onToggle,
    count,
    children,
}: CollapsibleSectionProps) {
    return (
        <div className="space-y-1">
            <button
                onClick={onToggle}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs font-medium text-foreground-muted uppercase tracking-wider hover:bg-surface-hover rounded-lg transition-colors"
            >
                {isOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                )}
                {icon}
                <span className="flex-1 text-left">{title}</span>
                <span className="text-foreground-muted/60">{count}</span>
            </button>
            {isOpen && (
                <div className="space-y-0.5 pl-2">
                    {children}
                </div>
            )}
        </div>
    );
}

// ============================================
// VIEW FORM DIALOG
// ============================================

interface ViewFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: string;
    editingView?: SavedView | null;
    currentConfig?: ViewConfig;
    onSuccess?: (view: SavedView) => void;
    /** t15: invoked after a new view is created so the parent can do
        post-create UX (open Add Column dialog for KANBAN, etc.). */
    onViewCreated?: (view: SavedView) => void;
    viewType: ViewType;
    customFields?: CustomField[];
}

function ViewFormDialog({
    open,
    onOpenChange,
    projectId,
    editingView,
    currentConfig,
    onSuccess,
    onViewCreated,
    viewType,
    customFields = [],
}: ViewFormDialogProps) {
    const { t } = useI18n();
    const createView = useCreateSavedView(projectId);
    const updateView = useUpdateSavedView(projectId);
    const toggleFavorite = useToggleFavoriteView(projectId);

    const isEditing = !!editingView;
    const isLoading = createView.isPending || updateView.isPending;

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<CreateSavedViewInput>({
        resolver: zodResolver(createSavedViewSchema),
        defaultValues: {
            name: editingView?.name || '',
            isDefault: editingView?.isDefault || false,
            isShared: editingView?.isShared || false,
            addToFavorites: editingView?.isFavorite || false,
            type: editingView?.type || viewType,
            config: editingView?.config || currentConfig || {},
            visibleCardFields: editingView?.visibleCardFields || [],
        },
    });

    const isDefault = watch('isDefault');
    const isShared = watch('isShared');
    const addToFavorites = watch('addToFavorites');
    const visibleCardFields = watch('visibleCardFields') || [];

    // R3 fix: reset form whenever editingView OR viewType changes.
    // The previous useEffect only watched editingView, so when the user
    // toggled the mode (Table → Calendar) the dialog's defaultValues.type
    // stayed pinned to the FIRST loaded mode's type and the new view got
    // persisted with the wrong type. handleOpenChange already does this
    // on dialog open, but the prop change can race the user's click —
    // this effect covers both paths.
    useEffect(() => {
        if (editingView) {
            reset({
                name: editingView.name,
                isDefault: editingView.isDefault,
                isShared: editingView.isShared,
                addToFavorites: editingView.isFavorite,
                type: editingView.type,
                config: editingView.config,
                visibleCardFields: editingView.visibleCardFields || [],
            });
        } else {
            // New-view path: always align type with the current viewType so
            // the form reflects what the user is actually looking at.
            reset({
                name: '',
                isDefault: false,
                isShared: false,
                addToFavorites: false,
                type: viewType,
                config: currentConfig || {},
                visibleCardFields: [],
            });
        }
    }, [editingView, viewType, currentConfig, reset]);

    const onSubmit = useCallback(async (data: CreateSavedViewInput) => {
        try {
            let result: SavedView;
            if (isEditing && editingView) {
                // t12 fix: merge the form's data.config with the
                // existing view's full config so backend PATCH
                // (which replaces config wholesale) doesn't wipe
                // anything the form doesn't surface — most
                // importantly kanbanColumns on Kanban views.
                const mergedConfig = {
                    ...(editingView.config || {}),
                    ...(data.config || {}),
                };
                result = await updateView.mutateAsync({
                    viewId: editingView.id,
                    data: {
                        ...data,
                        config: mergedConfig,
                    },
                });
                // Handle favorite status change separately
                const shouldBeFavorite = data.addToFavorites;
                const isFavorite = editingView.isFavorite;
                if (shouldBeFavorite !== isFavorite) {
                    await toggleFavorite.mutateAsync(editingView.id);
                }
            } else {
                // R3 fix: force `type` to the CURRENT viewType. Even with
                // the useEffect reset, data.type could lag behind the
                // prop if the user changed modes between the effect and
                // submit. Falling back to viewType is the safe pick.
                result = await createView.mutateAsync({
                    ...data,
                    type: viewType,
                    config: currentConfig || data.config,
                });
            }
            reset();
            onOpenChange(false);
            onSuccess?.(result);
            // t15 Part B.3: parent handles the post-create UX (open the
            // Add Column dialog when the new view is a Kanban, etc.).
            onViewCreated?.(result);
        } catch (error) {
            console.error('Error saving view:', error);
        }
    }, [isEditing, editingView, createView, updateView, toggleFavorite, currentConfig, reset, onOpenChange, onSuccess, onViewCreated, viewType]);

    // Reset form when dialog opens. Aligned with the useEffect above so
    // the type always matches the current viewType at the moment of
    // submit, regardless of whether viewType toggled before or after
    // the dialog mounted.
    const handleOpenChange = useCallback((newOpen: boolean) => {
        if (newOpen) {
            reset({
                name: editingView?.name || '',
                isDefault: editingView?.isDefault || false,
                isShared: editingView?.isShared || false,
                addToFavorites: editingView?.isFavorite || false,
                type: editingView?.type || viewType,
                config: editingView?.config || currentConfig || {},
                visibleCardFields: editingView?.visibleCardFields || [],
            });
        }
        onOpenChange(newOpen);
    }, [editingView, currentConfig, reset, onOpenChange, viewType]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogHeader>
                        <DialogTitle>{isEditing ? t('views.editViewTitle') : t('views.saveView')}</DialogTitle>
                        <DialogDescription>
                            {isEditing
                                ? t('views.editViewDescription')
                                : t('views.saveViewDescription')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t('views.viewName')}</Label>
                            <Input
                                id="name"
                                {...register('name')}
                                placeholder={t('views.viewNamePlaceholder')}
                            />
                            {errors.name && (
                                <p className="text-xs text-destructive">{errors.name.message}</p>
                            )}
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="isDefault"
                                checked={isDefault}
                                onCheckedChange={(checked) => setValue('isDefault', checked === true)}
                            />
                            <Label htmlFor="isDefault" className="text-sm font-normal">
                                {t('views.setAsDefault')}
                            </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="isShared"
                                checked={isShared}
                                onCheckedChange={(checked) => setValue('isShared', checked === true)}
                            />
                            <Label htmlFor="isShared" className="text-sm font-normal">
                                {t('views.shareWithTeam')}
                            </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="addToFavorites"
                                checked={addToFavorites}
                                onCheckedChange={(checked) => setValue('addToFavorites', checked === true)}
                            />
                            <Label htmlFor="addToFavorites" className="text-sm font-normal">
                                {isEditing ? t('views.isFavorite') : t('views.addToFavorites')}
                            </Label>
                        </div>

                        {/* Kanban-only: card content picker. The picker only
                            shows renderable custom fields; for Table views
                            the picker would just be a no-op, so we hide
                            the whole block. */}
                        {viewType === 'KANBAN' && (
                            <div className="space-y-2 border-t border-white/10 pt-3">
                                <Label className="text-sm font-medium">
                                    {t('kanban.cardFieldsTitle')}
                                </Label>
                                <KanbanCardFieldsPicker
                                    customFields={customFields}
                                    selectedFieldIds={visibleCardFields}
                                    onChange={(next) => setValue('visibleCardFields', next)}
                                />
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : isEditing ? (
                                t('common.saveChanges')
                            ) : (
                                t('views.saveView')
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ============================================
// SAVED VIEWS PANEL
// ============================================

interface SavedViewsPanelProps {
    projectId: string;
    activeViewId?: string | null;
    currentConfig?: ViewConfig;
    onViewSelect: (view: SavedView | null) => void;
    openSaveDialog?: boolean;
    onSaveDialogChange?: (open: boolean) => void;
    // Filter the panel to one view type so Kanban and Table views never
    // appear in the same list. The Kanban/Table toggle on the project
    // page feeds this prop.
    viewType: ViewType;
    customFields?: CustomField[];
    /** t15: invoked after a new view is created. The parent uses this
        to (a) immediately re-open the Add Column dialog when the new
        view is a KANBAN, and (b) keep its own active-view bookkeeping in
        sync without us calling onViewSelect redundantly. */
    onViewCreated?: (view: SavedView) => void;
}

export function SavedViewsPanel({
    projectId,
    activeViewId,
    currentConfig,
    onViewSelect,
    openSaveDialog,
    onSaveDialogChange,
    viewType,
    customFields = [],
    onViewCreated,
}: SavedViewsPanelProps) {
    const { t } = useI18n();
    const { user } = useAuthStore();
    // Always fetch with the active type filter; the backend transparently
    // seeds a default Kanban view the first time ?type=KANBAN is hit so
    // the user sees a usable board without any extra UI plumbing.
    const { data, isLoading } = useSavedViews(projectId, viewType);
    const deleteView = useDeleteSavedView(projectId);
    const duplicateView = useDuplicateSavedView(projectId);
    const updateView = useUpdateSavedView(projectId);
    const reorderViews = useReorderViews(projectId);
    const toggleFavorite = useToggleFavoriteView(projectId);
    const reorderFavorites = useReorderFavoriteViews(projectId);

    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingView, setEditingView] = useState<SavedView | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [viewToDelete, setViewToDelete] = useState<string | null>(null);
    const [draggedView, setDraggedView] = useState<SavedView | null>(null);
    const [dragCategory, setDragCategory] = useState<'favorites' | 'personal' | 'shared' | null>(null);

    // Section collapsed states - Favorites open by default, others closed
    const [sectionsOpen, setSectionsOpen] = useState({
        favorites: true,
        personal: false,
        shared: false,
    });

    // Sync external dialog state
    const dialogOpen = openSaveDialog !== undefined ? openSaveDialog : formDialogOpen;
    const setDialogOpen = (open: boolean) => {
        if (onSaveDialogChange) {
            onSaveDialogChange(open);
        }
        setFormDialogOpen(open);
    };

    // Categorize views
    const { favoriteViews, personalViews, sharedViews } = useMemo(() => {
        const views = data?.views || [];
        const currentUserId = user?.id;

        const favorites = views
            .filter(v => v.isFavorite)
            .sort((a, b) => (a.favoritePosition ?? 0) - (b.favoritePosition ?? 0));

        const personal = views
            .filter(v => !v.isFavorite && v.userId === currentUserId && !v.isShared)
            .sort((a, b) => a.position - b.position);

        const shared = views
            .filter(v => !v.isFavorite && v.isShared)
            .sort((a, b) => a.position - b.position);

        return {
            favoriteViews: favorites,
            personalViews: personal,
            sharedViews: shared,
        };
    }, [data?.views, user?.id]);

    const toggleSection = useCallback((section: 'favorites' | 'personal' | 'shared') => {
        setSectionsOpen(prev => ({
            ...prev,
            [section]: !prev[section],
        }));
    }, []);

    const handleCreateView = useCallback(() => {
        setEditingView(null);
        setDialogOpen(true);
    }, []);

    const handleEditView = useCallback((view: SavedView) => {
        setEditingView(view);
        setDialogOpen(true);
    }, []);

    const handleDeleteClick = useCallback((viewId: string) => {
        setViewToDelete(viewId);
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!viewToDelete) return;

        try {
            await deleteView.mutateAsync(viewToDelete);
            if (activeViewId === viewToDelete) {
                onViewSelect(null);
            }
            setDeleteDialogOpen(false);
            setViewToDelete(null);
        } catch (error) {
            console.error('Error deleting view:', error);
        }
    }, [viewToDelete, deleteView, activeViewId, onViewSelect]);

    const handleDuplicate = useCallback(async (viewId: string) => {
        try {
            await duplicateView.mutateAsync(viewId);
        } catch (error) {
            console.error('Error duplicating view:', error);
        }
    }, [duplicateView]);

    const handleSetDefault = useCallback(async (view: SavedView) => {
        try {
            await updateView.mutateAsync({
                viewId: view.id,
                data: { isDefault: true },
            });
        } catch (error) {
            console.error('Error setting default view:', error);
        }
    }, [updateView]);

    const handleToggleFavorite = useCallback(async (viewId: string) => {
        try {
            await toggleFavorite.mutateAsync(viewId);
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    }, [toggleFavorite]);

    // Drag and drop handlers
    const handleDragStart = useCallback((e: React.DragEvent, view: SavedView, category: 'favorites' | 'personal' | 'shared') => {
        setDraggedView(view);
        setDragCategory(category);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', view.id);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, targetView: SavedView, targetCategory: 'favorites' | 'personal' | 'shared') => {
        e.preventDefault();
        if (!draggedView || draggedView.id === targetView.id || dragCategory !== targetCategory) {
            setDraggedView(null);
            setDragCategory(null);
            return;
        }

        let sourceViews: SavedView[];
        if (targetCategory === 'favorites') {
            sourceViews = favoriteViews;
        } else if (targetCategory === 'personal') {
            sourceViews = personalViews;
        } else {
            sourceViews = sharedViews;
        }

        const viewIds = sourceViews.map(v => v.id);
        const fromIndex = viewIds.indexOf(draggedView.id);
        const toIndex = viewIds.indexOf(targetView.id);

        if (fromIndex !== -1 && toIndex !== -1) {
            const newViewIds = [...viewIds];
            newViewIds.splice(fromIndex, 1);
            newViewIds.splice(toIndex, 0, draggedView.id);

            if (targetCategory === 'favorites') {
                reorderFavorites.mutate(newViewIds);
            } else {
                reorderViews.mutate(newViewIds);
            }
        }

        setDraggedView(null);
        setDragCategory(null);
    }, [draggedView, dragCategory, favoriteViews, personalViews, sharedViews, reorderViews, reorderFavorites]);

    const handleDragEnd = useCallback(() => {
        setDraggedView(null);
        setDragCategory(null);
    }, []);

    const renderViewItem = (view: SavedView, category: 'favorites' | 'personal' | 'shared') => (
        <ViewItem
            key={view.id}
            view={view}
            isActive={activeViewId === view.id}
            onSelect={onViewSelect}
            onEdit={handleEditView}
            onDelete={handleDeleteClick}
            onDuplicate={handleDuplicate}
            onSetDefault={handleSetDefault}
            onToggleFavorite={handleToggleFavorite}
            isDragging={draggedView?.id === view.id}
            onDragStart={(e, v) => handleDragStart(e, v, category)}
            onDragOver={handleDragOver}
            onDrop={(e, v) => handleDrop(e, v, category)}
            onDragEnd={handleDragEnd}
            isOwner={view.userId === user?.id}
        />
    );

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2">
                <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
                    {t('views.savedViews')}
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleCreateView}
                    title={t('views.saveCurrentView')}
                >
                    <Plus className="h-4 w-4" />
                </Button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
                </div>
            ) : (
                <div className="space-y-2 px-1">
                    {/* R24 (Document mode): DOCUMENT no longer renders a
                        virtual default entry at all. "All tasks" is
                        meaningless for a document editor (there are no
                        tasks in a document) and the old synthetic
                        "General" entry mapped to `activeViewId === null`
                        — a local-only scratchpad that was never saved,
                        which is exactly the entry the user asked us to
                        remove. Document mode therefore starts on a REAL
                        DOCUMENT saved view: project-detail.tsx
                        auto-selects the default/favorite/first DOCUMENT
                        view whenever the selection is null or stale, and
                        DocumentView shows its "create your first
                        document" empty state when there is none.
                        Every other mode keeps the original "All tasks"
                        entry, which maps to `activeViewId === null`. */}
                    {viewType !== 'DOCUMENT' && (
                        <div
                            className={`flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${!activeViewId
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-surface-hover text-foreground-secondary'
                                }`}
                            onClick={() => onViewSelect(null)}
                            data-testid="saved-views-default-all-tasks"
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                    {t('views.allTasks')}
                                </span>
                            </div>
                            {!activeViewId && <Check className="h-4 w-4" />}
                        </div>
                    )}

                    {/* Favorites Section */}
                    <CollapsibleSection
                        title={t('views.favorites')}
                        icon={<Star className="h-3.5 w-3.5 text-amber-500" />}
                        isOpen={sectionsOpen.favorites}
                        onToggle={() => toggleSection('favorites')}
                        count={favoriteViews.length}
                    >
                        {favoriteViews.length === 0 ? (
                            <div className="px-2 py-2 text-xs text-foreground-muted">
                                {t('views.noFavorites')}
                            </div>
                        ) : (
                            favoriteViews.map(view => renderViewItem(view, 'favorites'))
                        )}
                    </CollapsibleSection>

                    {/* Personal Section */}
                    <CollapsibleSection
                        title={t('views.personal')}
                        icon={<User className="h-3.5 w-3.5" />}
                        isOpen={sectionsOpen.personal}
                        onToggle={() => toggleSection('personal')}
                        count={personalViews.length}
                    >
                        {personalViews.length === 0 ? (
                            <div className="px-2 py-2 text-xs text-foreground-muted">
                                {t('views.noPersonalViews')}
                            </div>
                        ) : (
                            personalViews.map(view => renderViewItem(view, 'personal'))
                        )}
                    </CollapsibleSection>

                    {/* Shared Section */}
                    <CollapsibleSection
                        title={t('views.shared')}
                        icon={<Share2 className="h-3.5 w-3.5 text-blue-500" />}
                        isOpen={sectionsOpen.shared}
                        onToggle={() => toggleSection('shared')}
                        count={sharedViews.length}
                    >
                        {sharedViews.length === 0 ? (
                            <div className="px-2 py-2 text-xs text-foreground-muted">
                                {t('views.noSharedViews')}
                            </div>
                        ) : (
                            sharedViews.map(view => renderViewItem(view, 'shared'))
                        )}
                    </CollapsibleSection>
                </div>
            )}

            {/* View form dialog */}
            <ViewFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                projectId={projectId}
                editingView={editingView}
                currentConfig={currentConfig}
                viewType={viewType}
                customFields={customFields}
                onSuccess={(view) => {
                    if (!editingView) {
                        onViewSelect(view);
                    }
                }}
                // R2: forward the parent's onViewCreated so it can
                // set activeViewId / open the Add Column dialog across
                // every mode (TABLE / CALENDAR / GANTT / KANBAN).
                onViewCreated={onViewCreated}
            />

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('views.deleteViewTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('views.deleteViewConfirmation')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteView.isPending}
                        >
                            {deleteView.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                t('common.delete')
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
